<div class="gs" [class.gs--open]="open" [ngStyle]="{'padding-left': '0%'}">

    <!-- ── The field ── -->
    <div class="gs-field" [class.gs-field--active]="open">
        <svg class="gs-field-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" stroke-width="1.7"/>
            <path d="M13.5 13.5L17 17" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
        </svg>

        <input #searchInput class="gs-input" type="text" autocomplete="off" spellcheck="false"
            placeholder="Search expenses, contacts, transactions or an amount"
            aria-label="Search expenses, contacts and transactions"
            role="combobox" aria-autocomplete="list" aria-controls="gs-listbox"
            [attr.aria-expanded]="open"
            [attr.aria-activedescendant]="activeIndex >= 0 ? 'gs-opt-' + activeIndex : null"
            [ngModel]="query" (ngModelChange)="onQueryChange($event)"
            (focus)="onFocus()" (keydown)="onInputKeydown($event)">

        <span class="gs-spinner" *ngIf="loading" aria-hidden="true"></span>

        <button type="button" class="gs-clear" *ngIf="query" (click)="clear()" title="Clear (Esc)" aria-label="Clear search">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M4.5 4.5l7 7M11.5 4.5l-7 7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
            </svg>
        </button>

        <kbd class="gs-kbd" *ngIf="!query && !open" aria-hidden="true">{{shortcutLabel}}</kbd>
    </div>

    <!-- ── The palette ── -->
    <div class="gs-panel" *ngIf="open" id="gs-listbox" role="listbox" aria-label="Search results">

        <!-- Kinds, with how many of each match -->
        <div class="gs-tabs" *ngIf="hasQuery && !tooShort" role="tablist">
            <button type="button" class="gs-tab" *ngFor="let t of tabs; trackBy: trackTab" role="tab"
                [class.gs-tab--active]="tab === t.key" [attr.aria-selected]="tab === t.key"
                (mousedown)="$event.preventDefault()" (click)="setTab(t.key)">
                {{t.label}}
                <span class="gs-tab-count" *ngIf="t.count !== null">{{t.count > 999 ? '999+' : t.count}}</span>
            </button>
        </div>

        <div class="gs-progress" [class.gs-progress--on]="loading"></div>

        <div class="gs-note" *ngIf="hasQuery && narrowedNote">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="8" cy="5.5" r="2.5" stroke="currentColor" stroke-width="1.3"/>
                <path d="M3 13.5c0-2.76 2.24-5 5-5s5 2.24 5 5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
            </svg>
            {{narrowedNote}}
        </div>

        <div class="gs-body" #panelBody>

            <!-- Too short to search -->
            <div class="gs-state" *ngIf="tooShort">
                Keep typing — search starts at two characters.
            </div>

            <!-- First search, nothing back yet -->
            <div class="gs-skeleton" *ngIf="hasQuery && !tooShort && loading && !response">
                <div class="gs-skeleton-row" *ngFor="let i of [0, 1, 2, 3]">
                    <span class="gs-skeleton-avatar"></span>
                    <span class="gs-skeleton-lines"><span></span><span></span></span>
                </div>
            </div>

            <!-- The search failed -->
            <div class="gs-state gs-state--error" *ngIf="hasQuery && failed && !loading">
                <strong>Search isn't responding.</strong>
                <span>Check your connection and try again.</span>
                <button type="button" class="gs-link" (mousedown)="$event.preventDefault()" (click)="retry()">Try again</button>
            </div>

            <!-- Nothing matched -->
            <div class="gs-state" *ngIf="showEmpty">
                <strong>No matches for “{{query.trim()}}”</strong>
                <span>Try a contact, a bill number, words from a bank narration, or an amount like 18.06.</span>
            </div>

            <!-- Empty box: nothing to show but the AI row -->
            <div class="gs-state gs-state--hint" *ngIf="!hasQuery && sections.length <= 1 && !recentSearches.length && !quickActions.length">
                Search across your expenses, contacts and bank transactions.
            </div>

            <ng-container *ngFor="let section of sections">
                <div class="gs-section" *ngIf="section.rows.length" [class.gs-section--ai]="section.key === 'ai'">
                    <div class="gs-section-head" *ngIf="section.label">
                        <span>{{section.label}}</span>
                        <span class="gs-section-count" *ngIf="section.total">{{section.total}}</span>
                        <button type="button" class="gs-link gs-link--quiet" *ngIf="section.key === 'recent'"
                            (mousedown)="$event.preventDefault()" (click)="clearRecents()">Clear</button>
                    </div>

                    <div class="gs-row" *ngFor="let row of section.rows; trackBy: trackRow"
                        role="option" [attr.id]="'gs-opt-' + row.index" [attr.aria-selected]="row.index === activeIndex"
                        [class.gs-row--active]="row.index === activeIndex"
                        [ngClass]="'gs-row--' + row.kind"
                        (mousemove)="activeIndex = row.index"
                        (mousedown)="$event.preventDefault()" (click)="openRow(row)">

                        <!-- Leading mark -->
                        <app-supplier-avatar class="gs-avatar {{row.avatarClass}}" *ngIf="row.avatar"
                            [supplierId]="row.supplier?.supplierId" [supplierName]="row.expense?.supplierName || row.supplier?.name"><span>{{row.avatar | shortLogo}}</span></app-supplier-avatar>
                        <div class="gs-icon" *ngIf="!row.avatar" [ngSwitch]="row.icon" [ngClass]="'gs-icon--' + row.icon">
                            <svg *ngSwitchCase="'recent'" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.3"/><path d="M8 5v3.3l2 1.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
                            <svg *ngSwitchCase="'in'" viewBox="0 0 16 16" fill="none"><path d="M11.5 4.5l-7 7M4.5 6v5.5H10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            <svg *ngSwitchCase="'out'" viewBox="0 0 16 16" fill="none"><path d="M4.5 11.5l7-7M6 4.5h5.5V10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            <svg *ngSwitchCase="'inbox'" viewBox="0 0 16 16" fill="none"><path d="M2 9.5l1.6-5.2a1 1 0 0 1 1-.8h6.8a1 1 0 0 1 1 .8L14 9.5V12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V9.5z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M2 9.5h3.5l1 1.5h3l1-1.5H14" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg>
                            <svg *ngSwitchCase="'bills'" viewBox="0 0 16 16" fill="none"><rect x="3" y="1.5" width="10" height="13" rx="1.5" stroke="currentColor" stroke-width="1.3"/><path d="M5.5 5h5M5.5 8h5M5.5 11h3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
                            <svg *ngSwitchCase="'bank'" viewBox="0 0 16 16" fill="none"><path d="M2 6l6-3.5L14 6M3 6v6M6.3 6v6M9.7 6v6M13 6v6M2 13.5h12" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            <svg *ngSwitchCase="'more'" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            <span *ngSwitchCase="'ai'" class="gs-ai-mark">✦</span>
                        </div>

                        <!-- Text -->
                        <div class="gs-text">
                            <div class="gs-title">
                                <ng-container *ngFor="let seg of row.title"><mark *ngIf="seg.match">{{seg.text}}</mark><ng-container *ngIf="!seg.match">{{seg.text}}</ng-container></ng-container>
                            </div>
                            <div class="gs-meta" *ngIf="row.meta?.length">
                                <ng-container *ngFor="let seg of row.meta"><mark *ngIf="seg.match">{{seg.text}}</mark><ng-container *ngIf="!seg.match">{{seg.text}}</ng-container></ng-container>
                            </div>
                            <div class="gs-detail" *ngIf="row.detail">
                                <ng-container *ngIf="row.detailSegments">Line: <ng-container *ngFor="let seg of row.detailSegments"><mark *ngIf="seg.match">{{seg.text}}</mark><ng-container *ngIf="!seg.match">{{seg.text}}</ng-container></ng-container></ng-container>
                                <ng-container *ngIf="!row.detailSegments">{{row.detail}}</ng-container>
                            </div>
                        </div>

                        <!-- Trailing -->
                        <div class="gs-trail">
                            <span class="gs-amount" *ngIf="row.amount" [ngClass]="row.amountTone ? 'gs-amount--' + row.amountTone : ''">{{row.amount}}</span>
                            <span class="gs-chip" *ngIf="row.chip" [ngClass]="'gs-chip--' + row.chip.tone">{{row.chip.label}}</span>
                            <button type="button" class="gs-remove" *ngIf="row.kind === 'recent'" title="Remove" aria-label="Remove from recent searches"
                                (mousedown)="$event.preventDefault()" (click)="removeRecent(row.recent, $event)">
                                <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4.5 4.5l7 7M11.5 4.5l-7 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                            </button>
                            <kbd class="gs-enter" *ngIf="row.index === activeIndex" aria-hidden="true">↵</kbd>
                        </div>
                    </div>
                </div>
            </ng-container>
        </div>

        <!-- Keyboard hints -->
        <div class="gs-footer" aria-hidden="true">
            <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
            <span><kbd>↵</kbd> open</span>
            <span *ngIf="hasQuery"><kbd>Tab</kbd> next type</span>
            <span><kbd>Esc</kbd> {{query ? 'clear' : 'close'}}</span>
            <span class="gs-footer-shortcut"><kbd>{{shortcutLabel}}</kbd> from anywhere</span>
        </div>
    </div>
</div>
